import { useParams, Link } from "react-router-dom";
import { ArrowRight, Phone } from "lucide-react";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/layout/Breadcrumbs";
import { useBlog, useBlogs } from "@/hooks/usePublicData";
import { useSite, telLink } from "@/context/SiteContext";
import NotFound from "@/pages/NotFound";

export default function BlogPost() {
  const { slug } = useParams();
  const s = useSite();
  const { data: post, isLoading, isError } = useBlog(slug);
  const { data: all = [] } = useBlogs();

  if (isError) return <NotFound />;
  if (isLoading || !post) return <div className="container-x py-24 text-center text-slate-400">Loading…</div>;

  const related = all.filter((b) => b.slug !== slug).slice(0, 3);
  const jsonLd = {
    "@context": "https://schema.org", "@type": "BlogPosting",
    headline: post.title, image: post.image?.startsWith("/") ? `https://chouhantaxiservice.com${post.image}` : post.image, author: { "@type": "Organization", name: "Chouhan Taxi Service" },
    publisher: { "@type": "Organization", name: "Chouhan Taxi Service", logo: { "@type": "ImageObject", url: `https://chouhantaxiservice.com${s.logo}` } },
  };

  return (
    <>
      <Seo title={post.meta_title || post.title} description={post.meta_description || post.excerpt} image={post.image} path={`/blog/${slug}`} type="article" jsonLd={jsonLd} published={post.created_at || undefined} />
      <Breadcrumbs items={[{ label: "Blog", to: "/blog" }, { label: post.title }]} />
      <article className="section-pad pt-8">
        <div className="container-x max-w-3xl">
          <div className="text-xs text-brand font-semibold uppercase tracking-wider mb-3">{post.category}</div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-extrabold text-navy-heading leading-tight">{post.title}</h1>
          <img src={post.image} alt={post.title} className="mt-7 rounded-2xl w-full h-64 sm:h-96 object-cover" />
          <div className="mt-8 prose prose-slate max-w-none">
            <p className="text-lg text-slate-600 leading-relaxed">{post.body}</p>
          </div>
          <div className="mt-8 bg-brand-light rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div><h3 className="font-heading font-bold text-navy-heading text-lg">Ready to travel?</h3><p className="text-sm text-slate-600">Book your taxi with Chouhan Taxi Service today.</p></div>
            <div className="flex gap-3">
              <Link to="/book-taxi" className="inline-flex items-center gap-2 bg-brand text-white font-semibold px-5 py-2.5 rounded-full text-sm">Book a Taxi <ArrowRight className="w-4 h-4" /></Link>
              <a href={telLink(s)} className="inline-flex items-center gap-2 bg-white text-navy font-semibold px-5 py-2.5 rounded-full text-sm border border-slate-200"><Phone className="w-4 h-4" /> Call</a>
            </div>
          </div>

          {related.length > 0 && (
            <div className="mt-12">
              <h2 className="font-heading font-bold text-navy-heading text-xl mb-4">Related Guides</h2>
              <div className="grid sm:grid-cols-3 gap-4">
                {related.map((b) => (
                  <Link key={b.slug} to={`/blog/${b.slug}`} className="group">
                    <img src={b.image} alt={b.title} loading="lazy" className="rounded-xl h-28 w-full object-cover mb-2" />
                    <div className="text-sm font-semibold text-navy-heading group-hover:text-brand transition-colors leading-snug">{b.title}</div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>
    </>
  );
}
